import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, RotateCcw, Image as ImageIcon, Check, X, AlertCircle } from 'lucide-react';
import MobileHeader from '../components/MobileHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BrandSettings = () => {
  const navigate = useNavigate();
  const { user, isGerencia } = useAuth();
  const fileInputRef = useRef(null);
  
  const [brandConfig, setBrandConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now());

  useEffect(() => {
    if (!isGerencia()) {
      toast.error('No tienes permiso para acceder a esta sección');
      navigate('/dashboard');
      return;
    }
    fetchBrandConfig();
  }, [isGerencia, navigate]);

  const fetchBrandConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/brand/logo`);
      setBrandConfig(response.data);
      setLogoTimestamp(Date.now());
    } catch (error) {
      toast.error('Error al cargar la configuración de marca');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar formato
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato no permitido. Use PNG, JPG, SVG o WEBP');
      return;
    }

    // Validar tamaño (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo excede el tamaño máximo de 5 MB');
      return;
    }

    setSelectedFile(file);
    
    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      await axios.post(`${API_URL}/admin/brand/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Logo actualizado exitosamente');
      setSelectedFile(null);
      setPreviewUrl(null);
      fetchBrandConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar el logo actual? Se restaurará el logo por defecto.')) {
      return;
    }

    try {
      setUploading(true);
      await axios.delete(`${API_URL}/admin/brand/logo`);
      toast.success('Logo eliminado. Se ha restaurado el logo por defecto.');
      fetchBrandConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm('¿Restaurar el logo anterior?')) {
      return;
    }

    try {
      setUploading(true);
      await axios.post(`${API_URL}/admin/brand/logo/restore`);
      toast.success('Logo restaurado exitosamente');
      fetchBrandConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar el logo');
    } finally {
      setUploading(false);
    }
  };

  const cancelPreview = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm uppercase tracking-widest text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header - Premium Black */}
      <header className="bg-black sticky top-0 z-10 hidden md:block">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/60 hover:text-white transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </button>
              <div className="w-px h-6 bg-white/20" />
              <h1 className="font-serif text-lg text-white">Configuración de Marca</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-black mb-2">Logo del Sistema</h1>
          <p className="text-sm text-muted-foreground">
            Administra el logo de ATELIER — JPM. Los cambios se reflejarán en toda la plataforma y en los PDFs generados.
          </p>
        </div>

        {/* Logo actual */}
        <div className="bg-white border border-border/40 shadow-sm p-6 mb-6">
          <h2 className="font-serif text-xl text-black mb-4">Logo Actual</h2>
          
          <div className="flex flex-col md:flex-row gap-6">
            {/* Preview del logo actual */}
            <div className="flex-shrink-0">
              <div className="w-64 h-48 bg-zinc-50 border border-border rounded-lg flex items-center justify-center overflow-hidden">
                {brandConfig?.logo_url ? (
                  <img
                    src={`${API_URL}/brand/logo/image?t=${logoTimestamp}`}
                    alt="Logo actual"
                    className="max-w-full max-h-full object-contain p-4"
                    data-testid="current-logo"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-xs uppercase tracking-widest">Sin logo</p>
                  </div>
                )}
              </div>
            </div>

            {/* Info del logo */}
            <div className="flex-1">
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Archivo</dt>
                  <dd className="text-black">{brandConfig?.logo_filename || 'No disponible'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-widest text-muted-foreground">Formato</dt>
                  <dd className="text-black uppercase">{brandConfig?.logo_format || 'N/A'}</dd>
                </div>
                {brandConfig?.updated_at && (
                  <div>
                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">Última actualización</dt>
                    <dd className="text-black">
                      {new Date(brandConfig.updated_at).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </dd>
                  </div>
                )}
                {brandConfig?.updated_by_username && (
                  <div>
                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">Actualizado por</dt>
                    <dd className="text-black">{brandConfig.updated_by_username}</dd>
                  </div>
                )}
              </dl>

              {/* Indicador de respaldo */}
              {brandConfig?.has_backup && (
                <div className="mt-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">
                  <AlertCircle className="w-4 h-4" />
                  <span>Hay un logo anterior disponible para restaurar</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cambiar logo */}
        <div className="bg-white border border-border/40 shadow-sm p-6 mb-6">
          <h2 className="font-serif text-xl text-black mb-4">Cambiar Logo</h2>
          
          {/* Área de selección / preview */}
          {previewUrl ? (
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Vista previa del nuevo logo</p>
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <div className="w-64 h-48 bg-zinc-900 border border-border rounded-lg flex items-center justify-center overflow-hidden">
                  <img
                    src={previewUrl}
                    alt="Vista previa"
                    className="max-w-full max-h-full object-contain p-4"
                    data-testid="preview-logo"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-black">{selectedFile?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile?.size / 1024).toFixed(1)} KB
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="flex items-center gap-2 bg-black text-white hover:bg-zinc-800 px-4 py-2 text-xs uppercase tracking-widest transition-all disabled:opacity-50"
                      data-testid="confirm-upload-button"
                    >
                      <Check className="w-4 h-4" />
                      {uploading ? 'Guardando...' : 'Confirmar'}
                    </button>
                    <button
                      onClick={cancelPreview}
                      disabled={uploading}
                      className="flex items-center gap-2 bg-white text-black border border-border hover:bg-zinc-50 px-4 py-2 text-xs uppercase tracking-widest transition-all disabled:opacity-50"
                      data-testid="cancel-preview-button"
                    >
                      <X className="w-4 h-4" />
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-6">
              <label
                htmlFor="logo-upload"
                className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-black transition-colors bg-zinc-50 hover:bg-zinc-100"
                data-testid="upload-area"
              >
                <Upload className="w-10 h-10 text-muted-foreground mb-3" />
                <p className="text-sm text-black mb-1">Haz clic para seleccionar un archivo</p>
                <p className="text-xs text-muted-foreground">PNG, JPG, SVG o WEBP (máx. 5 MB)</p>
              </label>
              <input
                id="logo-upload"
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>
          )}

          {/* Info de formatos */}
          <div className="bg-zinc-50 rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Formatos aceptados</p>
            <div className="flex flex-wrap gap-2">
              {['PNG', 'JPG', 'SVG', 'WEBP'].map((format) => (
                <span key={format} className="px-2 py-1 bg-white border border-border text-xs">
                  {format}
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Para mejores resultados, use un logo con fondo transparente (PNG) o vectorial (SVG).
            </p>
          </div>
        </div>

        {/* Acciones adicionales */}
        <div className="bg-white border border-border/40 shadow-sm p-6">
          <h2 className="font-serif text-xl text-black mb-4">Acciones</h2>
          
          <div className="flex flex-wrap gap-4">
            {brandConfig?.has_backup && (
              <button
                onClick={handleRestore}
                disabled={uploading}
                className="flex items-center gap-2 bg-amber-100 text-amber-900 hover:bg-amber-200 px-4 py-3 text-xs uppercase tracking-widest transition-all disabled:opacity-50"
                data-testid="restore-button"
              >
                <RotateCcw className="w-4 h-4" />
                Restaurar Logo Anterior
              </button>
            )}
            
            <button
              onClick={handleDelete}
              disabled={uploading}
              className="flex items-center gap-2 bg-white text-destructive border border-destructive hover:bg-destructive hover:text-white px-4 py-3 text-xs uppercase tracking-widest transition-all disabled:opacity-50"
              data-testid="delete-button"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar y Restaurar Default
            </button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-4">
            Al eliminar el logo actual, se restaurará automáticamente el logo por defecto del sistema.
          </p>
        </div>

        {/* Ubicaciones del logo */}
        <div className="mt-6 bg-zinc-50 border border-border/40 p-6 rounded-lg">
          <h3 className="font-serif text-lg text-black mb-3">¿Dónde aparece el logo?</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Pantalla de inicio de sesión
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Encabezado del sistema (Dashboard)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              PDF - Hoja de Medidas (Cliente)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              PDF - Ficha Interna
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              PDF - Expediente Completo
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default BrandSettings;
